// Enhanced Error Handling System for Mawered

import i18n from '../i18n';

const t = (key, options) => i18n.t(key, options);

/**
 * Error types and categories
 */
export const ErrorTypes = {
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  CONNECTION_TIMEOUT: 'CONNECTION_TIMEOUT',
  SERVER_UNREACHABLE: 'SERVER_UNREACHABLE',
  
  // Authentication errors
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  
  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_DATA: 'INVALID_DATA',
  REQUIRED_FIELD: 'REQUIRED_FIELD',
  
  // Business logic errors
  BUSINESS_RULE_VIOLATION: 'BUSINESS_RULE_VIOLATION',
  DUPLICATE_ENTRY: 'DUPLICATE_ENTRY',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  
  // Server errors
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  DATABASE_ERROR: 'DATABASE_ERROR',
  
  // Client errors
  JAVASCRIPT_ERROR: 'JAVASCRIPT_ERROR',
  COMPONENT_ERROR: 'COMPONENT_ERROR',
  
  // Unknown errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  LOW: 'low',       // Informational, user can continue
  MEDIUM: 'medium', // Warning, user should be aware
  HIGH: 'high',     // Error, user action required
  CRITICAL: 'critical' // Critical, app functionality affected
};

/**
 * User-friendly error messages
 */
const ErrorMessages = {
  [ErrorTypes.NETWORK_ERROR]: {
    titleKey: 'errors.networkTitle',
    messageKey: 'errors.networkMsg',
    severity: ErrorSeverity.HIGH,
    canRetry: true,
    icon: 'wifi-off'
  },
  [ErrorTypes.CONNECTION_TIMEOUT]: {
    titleKey: 'errors.timeoutTitle',
    messageKey: 'errors.timeoutMsg',
    severity: ErrorSeverity.MEDIUM,
    canRetry: true,
    icon: 'clock'
  },
  [ErrorTypes.SERVER_UNREACHABLE]: {
    titleKey: 'errors.serverUnreachableTitle',
    messageKey: 'errors.serverUnreachableMsg',
    severity: ErrorSeverity.HIGH,
    canRetry: true,
    icon: 'server-off'
  },
  [ErrorTypes.UNAUTHORIZED]: {
    titleKey: 'errors.unauthorizedTitle',
    messageKey: 'errors.unauthorizedMsg',
    severity: ErrorSeverity.HIGH,
    canRetry: false,
    icon: 'lock',
    action: 'LOGIN_REQUIRED'
  },
  [ErrorTypes.FORBIDDEN]: {
    titleKey: 'errors.forbiddenTitle',
    messageKey: 'errors.forbiddenMsg',
    severity: ErrorSeverity.MEDIUM,
    canRetry: false,
    icon: 'shield-x'
  },
  [ErrorTypes.TOKEN_EXPIRED]: {
    titleKey: 'errors.tokenExpiredTitle',
    messageKey: 'errors.tokenExpiredMsg',
    severity: ErrorSeverity.HIGH,
    canRetry: false,
    icon: 'key',
    action: 'LOGIN_REQUIRED'
  },
  [ErrorTypes.VALIDATION_ERROR]: {
    titleKey: 'errors.validationTitle',
    messageKey: 'errors.validationMsg',
    severity: ErrorSeverity.MEDIUM,
    canRetry: false,
    icon: 'alert-triangle'
  },
  [ErrorTypes.INVALID_DATA]: {
    titleKey: 'errors.invalidDataTitle',
    messageKey: 'errors.invalidDataMsg',
    severity: ErrorSeverity.MEDIUM,
    canRetry: false,
    icon: 'file-x'
  },
  [ErrorTypes.DUPLICATE_ENTRY]: {
    titleKey: 'errors.duplicateTitle',
    messageKey: 'errors.duplicateMsg',
    severity: ErrorSeverity.MEDIUM,
    canRetry: false,
    icon: 'copy'
  },
  [ErrorTypes.RESOURCE_NOT_FOUND]: {
    titleKey: 'errors.notFoundTitle',
    messageKey: 'errors.notFoundMsg',
    severity: ErrorSeverity.MEDIUM,
    canRetry: true,
    icon: 'search-x'
  },
  [ErrorTypes.INTERNAL_SERVER_ERROR]: {
    titleKey: 'errors.internalServerTitle',
    messageKey: 'errors.internalServerMsg',
    severity: ErrorSeverity.HIGH,
    canRetry: true,
    icon: 'server-crash'
  },
  [ErrorTypes.SERVICE_UNAVAILABLE]: {
    titleKey: 'errors.serviceUnavailableTitle',
    messageKey: 'errors.serviceUnavailableMsg',
    severity: ErrorSeverity.HIGH,
    canRetry: true,
    icon: 'tool'
  },
  [ErrorTypes.DATABASE_ERROR]: {
    titleKey: 'errors.databaseTitle',
    messageKey: 'errors.databaseMsg',
    severity: ErrorSeverity.HIGH,
    canRetry: true,
    icon: 'database-zap'
  },
  [ErrorTypes.JAVASCRIPT_ERROR]: {
    titleKey: 'errors.jsErrorTitle',
    messageKey: 'errors.jsErrorMsg',
    severity: ErrorSeverity.HIGH,
    canRetry: true,
    icon: 'bug',
    action: 'RELOAD_PAGE'
  },
  [ErrorTypes.UNKNOWN_ERROR]: {
    titleKey: 'errors.unknownTitle',
    messageKey: 'errors.unknownMsg',
    severity: ErrorSeverity.MEDIUM,
    canRetry: true,
    icon: 'help-circle'
  }
};

/**
 * Enhanced Error class with additional context
 */
export class EnhancedError extends Error {
  constructor(type, originalError = null, context = {}) {
    const errorConfig = ErrorMessages[type] || ErrorMessages[ErrorTypes.UNKNOWN_ERROR];
    super(t(errorConfig.messageKey));

    this.name = 'EnhancedError';
    this.type = type;
    this.title = t(errorConfig.titleKey);
    this.severity = errorConfig.severity;
    this.canRetry = errorConfig.canRetry;
    this.icon = errorConfig.icon;
    this.action = errorConfig.action;
    this.originalError = originalError;
    this.context = context;
    this.timestamp = new Date().toISOString();
    this.errorId = this.generateErrorId();
  }

  generateErrorId() {
    return Math.random().toString(36).substr(2, 9);
  }

  toJSON() {
    return {
      errorId: this.errorId,
      type: this.type,
      title: this.title,
      message: this.message,
      severity: this.severity,
      canRetry: this.canRetry,
      icon: this.icon,
      action: this.action,
      context: this.context,
      timestamp: this.timestamp,
      stack: this.stack
    };
  }
}

/**
 * Error Parser - converts different error types to EnhancedError
 */
export class ErrorParser {
  static parseApiError(error) {
    // Network errors
    if (!error.response) {
      if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
        return new EnhancedError(ErrorTypes.NETWORK_ERROR, error);
      }
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        return new EnhancedError(ErrorTypes.CONNECTION_TIMEOUT, error);
      }
      return new EnhancedError(ErrorTypes.SERVER_UNREACHABLE, error);
    }

    const { status, data } = error.response;
    const context = {
      status,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      data: error.config?.data
    };

    // Authentication errors
    if (status === 401) {
      if (data?.error === 'token_expired' || data?.message?.includes('expired')) {
        return new EnhancedError(ErrorTypes.TOKEN_EXPIRED, error, context);
      }
      return new EnhancedError(ErrorTypes.UNAUTHORIZED, error, context);
    }

    if (status === 403) {
      return new EnhancedError(ErrorTypes.FORBIDDEN, error, context);
    }

    // Client errors
    if (status === 400) {
      if (data?.error === 'validation_error' || data?.errors) {
        return new EnhancedError(ErrorTypes.VALIDATION_ERROR, error, {
          ...context,
          validationErrors: data.errors
        });
      }
      if (data?.error === 'duplicate_entry') {
        return new EnhancedError(ErrorTypes.DUPLICATE_ENTRY, error, context);
      }
      return new EnhancedError(ErrorTypes.INVALID_DATA, error, context);
    }

    if (status === 404) {
      return new EnhancedError(ErrorTypes.RESOURCE_NOT_FOUND, error, context);
    }

    if (status === 409) {
      return new EnhancedError(ErrorTypes.DUPLICATE_ENTRY, error, context);
    }

    if (status === 422) {
      return new EnhancedError(ErrorTypes.VALIDATION_ERROR, error, {
        ...context,
        validationErrors: data?.errors || data?.details
      });
    }

    // Server errors
    if (status === 500) {
      if (data?.error?.includes('database') || data?.message?.includes('database')) {
        return new EnhancedError(ErrorTypes.DATABASE_ERROR, error, context);
      }
      return new EnhancedError(ErrorTypes.INTERNAL_SERVER_ERROR, error, context);
    }

    if (status === 503) {
      return new EnhancedError(ErrorTypes.SERVICE_UNAVAILABLE, error, context);
    }

    // Unknown error
    return new EnhancedError(ErrorTypes.UNKNOWN_ERROR, error, context);
  }

  static parseJavaScriptError(error, errorInfo = null) {
    const context = {
      componentStack: errorInfo?.componentStack,
      errorBoundary: true
    };

    if (error.name === 'ChunkLoadError' || error.message.includes('chunk')) {
      return new EnhancedError(ErrorTypes.JAVASCRIPT_ERROR, error, {
        ...context,
        suggestion: t('errors.reloadSuggestion')
      });
    }

    return new EnhancedError(ErrorTypes.JAVASCRIPT_ERROR, error, context);
  }
}

/**
 * Error Handler - main error handling utility
 */
export class ErrorHandler {
  constructor(options = {}) {
    this.options = {
      enableLogging: true,
      enableReporting: false,
      maxRetries: 3,
      retryDelay: 1000,
      ...options
    };
    
    this.errorLog = [];
    this.retryAttempts = new Map();
  }

  /**
   * Handle an error and return user-friendly information
   */
  handleError(error, context = {}) {
    let enhancedError;

    // Convert to EnhancedError if not already
    if (error instanceof EnhancedError) {
      enhancedError = error;
    } else if (error.response || error.request) {
      enhancedError = ErrorParser.parseApiError(error);
    } else {
      enhancedError = ErrorParser.parseJavaScriptError(error);
    }

    // Add additional context
    enhancedError.context = { ...enhancedError.context, ...context };

    // Log error
    if (this.options.enableLogging) {
      this.logError(enhancedError);
    }

    // Report error (if reporting is enabled)
    if (this.options.enableReporting && enhancedError.severity === ErrorSeverity.CRITICAL) {
      this.reportError(enhancedError);
    }

    return enhancedError;
  }

  /**
   * Check if operation can be retried
   */
  canRetry(errorId, operation) {
    const key = `${operation}-${errorId}`;
    const attempts = this.retryAttempts.get(key) || 0;
    return attempts < this.options.maxRetries;
  }

  /**
   * Record retry attempt
   */
  recordRetryAttempt(errorId, operation) {
    const key = `${operation}-${errorId}`;
    const attempts = this.retryAttempts.get(key) || 0;
    this.retryAttempts.set(key, attempts + 1);
  }

  /**
   * Clear retry attempts for an operation
   */
  clearRetryAttempts(operation) {
    for (const [key] of this.retryAttempts.entries()) {
      if (key.startsWith(operation)) {
        this.retryAttempts.delete(key);
      }
    }
  }

  /**
   * Create retry function with exponential backoff
   */
  createRetryFunction(operation, originalArgs = []) {
    return async () => {
      const delay = this.options.retryDelay * Math.pow(2, this.retryAttempts.size);
      await new Promise(resolve => setTimeout(resolve, delay));
      return operation(...originalArgs);
    };
  }

  /**
   * Log error for debugging
   */
  logError(error) {
    this.errorLog.push({
      timestamp: new Date().toISOString(),
      error: error.toJSON()
    });

    // Keep only last 100 errors
    if (this.errorLog.length > 100) {
      this.errorLog.shift();
    }

    // Console log for development
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 ${error.title}`);
      console.error('Enhanced Error:', error);
      console.error('Original Error:', error.originalError);
      console.error('Context:', error.context);
      console.groupEnd();
    }
  }

  /**
   * Report error to external service
   */
  reportError(error) {
    // Implementation would send to error reporting service
    // like Sentry, LogRocket, etc.
    console.warn('Error reporting not implemented:', error.toJSON());
  }

  /**
   * Get error statistics
   */
  getErrorStats() {
    const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const recentErrors = this.errorLog.filter(
      log => new Date(log.timestamp) > last24h
    );

    const errorsByType = {};
    const errorsBySeverity = {};

    recentErrors.forEach(log => {
      const { type, severity } = log.error;
      errorsByType[type] = (errorsByType[type] || 0) + 1;
      errorsBySeverity[severity] = (errorsBySeverity[severity] || 0) + 1;
    });

    return {
      total: recentErrors.length,
      last24h: recentErrors.length,
      byType: errorsByType,
      bySeverity: errorsBySeverity
    };
  }

  /**
   * Clear all logged errors
   */
  clearErrorLog() {
    this.errorLog = [];
    this.retryAttempts.clear();
  }
}

/**
 * Global error handler instance
 */
export const globalErrorHandler = new ErrorHandler({
  enableLogging: true,
  enableReporting: process.env.NODE_ENV === 'production',
  maxRetries: 3,
  retryDelay: 1000
});

/**
 * Utility functions
 */
export const ErrorUtils = {
  /**
   * Check if error requires user authentication
   */
  requiresAuth: (error) => {
    return error instanceof EnhancedError && 
           (error.type === ErrorTypes.UNAUTHORIZED || error.type === ErrorTypes.TOKEN_EXPIRED);
  },

  /**
   * Check if error is network related
   */
  isNetworkError: (error) => {
    return error instanceof EnhancedError && [
      ErrorTypes.NETWORK_ERROR,
      ErrorTypes.CONNECTION_TIMEOUT,
      ErrorTypes.SERVER_UNREACHABLE
    ].includes(error.type);
  },

  /**
   * Check if error is retryable
   */
  isRetryable: (error) => {
    return error instanceof EnhancedError && error.canRetry;
  },

  /**
   * Get user-friendly message for error
   */
  getUserMessage: (error) => {
    if (error instanceof EnhancedError) {
      return {
        title: error.title,
        message: error.message,
        severity: error.severity
      };
    }
    
    return {
      title: t('errors.unexpectedTitle'),
      message: error.message || t('errors.genericMsg'),
      severity: ErrorSeverity.MEDIUM
    };
  }
}; 